import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import { breadcrumbSchema } from "@/lib/organization-schema";
import { FileText, Download, Eye, X } from "lucide-react";

interface Category {
  id: string;
  name: string;
  sort_order: number;
}

interface Document {
  id: string;
  title: string;
  description: string | null;
  file_path: string;
  file_name: string;
  created_at: string;
  category_id: string;
}

export default function Transparency() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!supabase) {
        setLoading(false);
        return;
      }
      const [catRes, docRes] = await Promise.all([
        supabase.from("document_categories").select("*").order("sort_order"),
        supabase.from("transparency_documents").select("*").order("created_at", { ascending: false }),
      ]);
      if (catRes.data) setCategories(catRes.data);
      if (docRes.data) setDocuments(docRes.data);
      setLoading(false);
    }
    load();
  }, []);

  function getPublicUrl(filePath: string) {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    return `https://${projectId}.supabase.co/functions/v1/serve-doc?path=${encodeURIComponent(filePath)}`;
  }

  const isPdf = (fileName: string) => fileName.toLowerCase().endsWith(".pdf");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Transparency — Abba's House Communities",
      description: "Public compliance documents, IRS filings, licenses, permits, and board motions for Abba's House Communities Inc.",
      url: "https://abbashouse.org/transparency",
      publisher: {
        "@type": "Organization",
        name: "Abba's House Communities Inc.",
      },
    },
    breadcrumbSchema([
      { name: "Home", url: "https://abbashouse.org/" },
      { name: "Transparency", url: "https://abbashouse.org/transparency" },
    ]),
  ];

  return (
    <main className="min-h-screen bg-background">
      <SEO
        title="Transparency"
        description="View public compliance documents including IRS filings, state filings, licenses, permits, and board motions for Abba's House Communities Inc."
        canonical="https://abbashouse.org/transparency"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-primary text-primary-foreground py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <p className="text-sm font-semibold tracking-[0.3em] uppercase opacity-50 mb-4">Accountability</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Transparency</h1>
          <p className="text-lg opacity-70 max-w-2xl mx-auto leading-relaxed">
            We believe in full transparency with our donors, partners, and the public. Below you'll find our compliance documents, filings, and board motions.
          </p>
        </div>
      </section>

      {/* Document sections */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          {loading ? (
            <p className="text-center text-muted-foreground py-12">Loading documents…</p>
          ) : categories.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No documents available at this time.</p>
          ) : (
            <div className="space-y-12">
              {categories.map((cat) => {
                const catDocs = documents.filter((d) => d.category_id === cat.id);
                return (
                  <div key={cat.id}>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6 border-b border-border pb-3">
                      {cat.name}
                    </h2>
                    {catDocs.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">No documents in this category yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {catDocs.map((doc) => {
                          const url = getPublicUrl(doc.file_path);
                          return (
                            <div key={doc.id} className="border border-border rounded-xl p-5 hover:border-foreground transition-colors bg-background group">
                              <div className="flex items-start gap-3">
                                <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                                <div className="min-w-0 flex-1">
                                  <h3 className="text-sm font-bold truncate">{doc.title}</h3>
                                  {doc.description && (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{doc.description}</p>
                                  )}
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Uploaded {new Date(doc.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                                  </p>
                                  <div className="flex items-center gap-3 mt-3">
                                    {isPdf(doc.file_name) && (
                                      <button
                                        onClick={() => setPreviewUrl(url)}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-70 transition-opacity"
                                      >
                                        <Eye className="h-3.5 w-3.5" /> Preview
                                      </button>
                                    )}
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-70 transition-opacity"
                                    >
                                      <Download className="h-3.5 w-3.5" /> Download
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
          <div className="bg-background rounded-xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="text-sm font-semibold">Document Preview</span>
              <button onClick={() => setPreviewUrl(null)} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <iframe src={previewUrl} className="flex-1 w-full" title="Document preview" />
          </div>
        </div>
      )}
    </main>
  );
}